export const DIRECT_UNATTRIBUTED_LABEL = "Direct / Unattributed";

/** @deprecated Use DIRECT_UNATTRIBUTED_LABEL */
export const UTM_NOT_SET = "(not set)";

const FALLBACK_ALIASES = new Set([
  "",
  UTM_NOT_SET,
  "Direct",
  DIRECT_UNATTRIBUTED_LABEL,
  "Unattributed",
  "Unknown Account",
  "Unknown Entry Point",
]);

export const normalizeAttributionFallback = (value) => {
  if (value == null || !String(value).trim()) return DIRECT_UNATTRIBUTED_LABEL;
  const trimmed = String(value).trim();
  if (FALLBACK_ALIASES.has(trimmed)) return DIRECT_UNATTRIBUTED_LABEL;
  return trimmed;
};

export const DIMENSION_UTM_FIELD = {
  platform: "source",
  account: "campaign",
  entryPoint: "medium",
};

export const ATTRIBUTION_ACCOUNTS = ["Nico", "Jordi", "BV1"];

export const ATTRIBUTION_ENTRY_POINTS = [
  "Bio Link",
  "Stories",
  "Story Reply",
  "ManyChat",
  "Email Sequence",
  "Email Broadcast",
  "YT Description",
  "Paid Ads",
];

const UTM_MEDIUM_ENTRY_POINT_MAPPINGS = {
  manychat: "ManyChat",
  "manychat+instagram": "ManyChat",
  bio: "Bio Link",
  "bio+instagram": "Bio Link",
  "bio+tiktok": "Bio Link",
  "bio+youtube": "Bio Link",
  "bio+facebook": "Bio Link",
  "community+skool": "Bio Link",
  stories: "Stories",
  "stories+instagram": "Stories",
  stories_reply: "Story Reply",
  "stories_reply+instagram": "Story Reply",
  sequence: "Email Sequence",
  "sequence+email": "Email Sequence",
  broadcast: "Email Broadcast",
  "broadcast+email": "Email Broadcast",
  description: "YT Description",
  "description+youtube": "YT Description",
  paid: "Paid Ads",
  "paid+youtube": "Paid Ads",
  "paid+tiktok": "Paid Ads",
  "paid+facebook": "Paid Ads",
};

export const resolveEntryPointFromUtmMedium = (raw) => {
  if (raw == null || !String(raw).trim()) return null;
  const normalized = String(raw).trim().toLowerCase();
  return UTM_MEDIUM_ENTRY_POINT_MAPPINGS[normalized] ?? null;
};

const UTM_CAMPAIGN_ACCOUNT_MAPPINGS = {
  nico: "Nico",
  jordi: "Jordi",
  bv1: "BV1",
};

export const resolveAccountFromUtmCampaign = (raw) => {
  if (raw == null || !String(raw).trim()) return null;
  const normalized = String(raw).trim().toLowerCase();
  if (UTM_CAMPAIGN_ACCOUNT_MAPPINGS[normalized]) {
    return UTM_CAMPAIGN_ACCOUNT_MAPPINGS[normalized];
  }
  if (normalized.startsWith("nico")) return "Nico";
  if (normalized.startsWith("jordi")) return "Jordi";
  if (normalized.startsWith("bv1")) return "BV1";
  return null;
};

export const formatBreakdownName = (name) => normalizeAttributionFallback(name);

export const formatAccountLabel = (name) => {
  const resolved = resolveAccountFromUtmCampaign(name);
  if (resolved) return resolved;
  if (ATTRIBUTION_ACCOUNTS.includes(name)) return name;
  return normalizeAttributionFallback(name);
};
export const formatEntryPointLabel = (name) => {
  const resolved = resolveEntryPointFromUtmMedium(name);
  if (resolved) return resolved;
  if (ATTRIBUTION_ENTRY_POINTS.includes(name)) return name;
  return normalizeAttributionFallback(name);
};

export const mapBreakdownRows = (rows, dimension) =>
  (rows ?? []).map((row) => ({
    ...row,
    name:
      dimension === "account"
        ? formatAccountLabel(row.name)
        : dimension === "entryPoint"
          ? formatEntryPointLabel(row.name)
          : formatBreakdownName(row.name),
  }));

export const isUnmappedAccount = (name) =>
  normalizeAttributionFallback(name) === DIRECT_UNATTRIBUTED_LABEL;

export const attributionConversionPercent = (contacts, sales) =>
  contacts > 0 ? Number(((sales / contacts) * 100).toFixed(1)) : 0;

export const buildAttributionRows = (drilldown) =>
  (drilldown?.rows ?? []).map((row) => {
    const contacts = row.contacts ?? 0;
    const sales = row.sales ?? 0;

    return {
      source: normalizeAttributionFallback(row.source),
      campaign: normalizeAttributionFallback(row.campaign),
      account: formatAccountLabel(row.account),
      contacts,
      sales,
      revenue: row.revenue ?? 0,
      conversion: attributionConversionPercent(contacts, sales),
    };
  });

export const normalizeBreakdownRow = (row) => {
  if (!row) return row;
  const contacts = row.contacts ?? 0;
  const sales = row.sales ?? 0;
  return {
    ...row,
    conversionRate: attributionConversionPercent(contacts, sales),
  };
};

export const mergeBreakdownRows = (rows) =>
  [...(rows ?? [])]
    .map((row) => normalizeBreakdownRow(row))
    .sort(
      (a, b) =>
        (b.attributedRevenue ?? 0) - (a.attributedRevenue ?? 0) ||
        normalizeAttributionFallback(a.name).localeCompare(normalizeAttributionFallback(b.name))
    );

export const filterAttributionRows = (rows, { platform, account, campaign }) =>
  rows.filter((row) => {
    if (platform && row.source !== platform) return false;
    if (account && row.account !== account) return false;
    if (campaign && row.campaign !== campaign) return false;
    return true;
  });

export const buildFilterOptions = (rows, utmLinks, breakdown) => {
  const platforms = new Set();
  const accounts = new Set();
  const campaigns = new Set();

  (breakdown?.byPlatform ?? []).forEach((r) => platforms.add(formatBreakdownName(r.name)));
  (breakdown?.byAccount ?? []).forEach((r) => {
    const label = formatAccountLabel(r.name);
    if (!isUnmappedAccount(label)) accounts.add(label);
  });
  (utmLinks ?? []).forEach((link) => {
    if (link.source) platforms.add(normalizeAttributionFallback(link.source));
    campaigns.add(normalizeAttributionFallback(link.campaign));
  });
  rows.forEach((row) => {
    platforms.add(normalizeAttributionFallback(row.source));
    if (!isUnmappedAccount(row.account)) accounts.add(row.account);
    campaigns.add(normalizeAttributionFallback(row.campaign));
  });

  return {
    platforms: [...platforms]
      .filter((v) => v && v !== DIRECT_UNATTRIBUTED_LABEL)
      .sort(),
    accounts: [...accounts].sort(),
    campaigns: [...campaigns]
      .filter((v) => v && v !== DIRECT_UNATTRIBUTED_LABEL)
      .sort(),
  };
};

export const computeInsights = (rows, breakdown) => {
  const platforms = [...(breakdown?.byPlatform ?? [])].sort(
    (a, b) =>
      (b.attributedRevenue ?? 0) - (a.attributedRevenue ?? 0) ||
      normalizeAttributionFallback(a.name).localeCompare(normalizeAttributionFallback(b.name))
  );
  const withData = platforms.filter((p) => (p.contacts ?? 0) > 0 || (p.sales ?? 0) > 0);

  const topSource = withData[0] ?? null;
  const worstSource = withData.length > 1 ? withData[withData.length - 1] : withData[0] ?? null;

  const campaignCandidates = rows.filter((r) => r.contacts > 0 || r.sales > 0);
  const highestRoiCampaign = [...campaignCandidates].sort((a, b) => {
    const roiA = a.contacts > 0 ? a.revenue / a.contacts : a.revenue;
    const roiB = b.contacts > 0 ? b.revenue / b.contacts : b.revenue;
    return roiB - roiA;
  })[0] ?? null;

  return { topSource, worstSource, highestRoiCampaign };
};

export const aggregateBySource = (rows) => {
  const grouped = new Map();

  rows.forEach((row) => {
    const source = normalizeAttributionFallback(row.source);
    const existing = grouped.get(source);
    if (existing) {
      existing.contacts += row.contacts;
      existing.sales += row.sales;
      existing.revenue += row.revenue;
      return;
    }
    grouped.set(source, { ...row, source });
  });

  return [...grouped.values()].map((row) => ({
    ...row,
    conversion: attributionConversionPercent(row.contacts ?? 0, row.sales ?? 0),
  }));
};
